package org.moxultimate.topwithmox.wallpapercrud.add

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.document
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vw
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.moxultimate.topwithmox.components.buttons.GradientButton
import org.moxultimate.topwithmox.components.buttons.PressableButtonStyle
import org.moxultimate.topwithmox.components.crud.*
import org.moxultimate.topwithmox.components.texts.moxTextGradient
import org.moxultimate.topwithmox.di.koinViewModel
import org.moxultimate.topwithmox.navigation.rememberAppNavigator
import org.moxultimate.topwithmox.theme.DarkTextColor
import org.moxultimate.topwithmox.theme.FontFamily
import org.moxultimate.topwithmox.wallpapercrud.main.WallpaperCRUDState
import org.moxultimate.topwithmox.wallpapercrud.main.WallpaperCRUDViewModel
import org.moxultimate.topwithmox.wallpapercrud.main.WallpaperFormEntry
import org.w3c.files.File

/** Full-page batch upload form for adding new wallpapers. Opens in its own tab from WallpaperCRUDPage. */
@Composable
fun WallpaperAddPage() {
    val viewModel: WallpaperCRUDViewModel = koinViewModel()
    val uploadState by viewModel.uploadState.collectAsState()
    val navigator = rememberAppNavigator()

    var entries by remember { mutableStateOf(listOf(WallpaperFormEntry())) }
    var globalTags by remember { mutableStateOf("") }
    var globalSeries by remember { mutableStateOf("") }
    // True after the user has clicked "Upload All" at least once — enables per-entry validation hints
    var submitAttempted by remember { mutableStateOf(false) }

    // Success dialog overlay — shown when upload succeeds
    if (uploadState is WallpaperCRUDState.Success) {
        CRUDSuccessDialog(
            message = (uploadState as WallpaperCRUDState.Success).message,
            onOkay = {
                viewModel.resetState()
                navigator.goToWallpaperCRUD()
            },
        )
    }

    // Recompute every entry's tags whenever globalTags changes
    LaunchedEffect(globalTags) {
        entries = entries.map { it.withAutoTags(globalTags) }
    }

    // Propagate globalSeries to every entry whenever it changes
    LaunchedEffect(globalSeries) {
        if (globalSeries.isNotBlank()) {
            entries = entries.map { it.copy(series = globalSeries).withAutoTags(globalTags) }
        }
    }

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .fillMaxHeight()
            .backgroundColor(Colors.White)
            .overflow(Overflow.Auto),
        contentAlignment = Alignment.TopCenter,
    ) {
        Column(
            modifier = Modifier
                .width(clamp(min = 900.px, value = 80.vw, max = 1400.px))
                .padding(top = 80.px, bottom = 32.px, leftRight = 2.cssRem),
            verticalArrangement = Arrangement.spacedBy(32.px),
        ) {
            // Page title
            SpanText(
                text = "Batch Upload Wallpapers",
                modifier = Modifier
                    .fontFamily(FontFamily)
                    .fontSize(32.px)
                    .fontWeight(FontWeight.Bold)
                    .moxTextGradient()
            )

            // Batch drop zone — drop multiple images here to auto-create entries
            BatchDropZone(
                onFilesDropped = { files ->
                    val grouped = groupFilesForBatch(files)

                    val newEntries = grouped.map { (imageFile, thumbnailFile, panoramaFile) ->
                        val rawTitle = when {
                            imageFile != null -> imageFile.name.substringBeforeLast(".")
                            thumbnailFile != null -> thumbnailFile.name.substringBeforeLast(".")
                                .replace(Regex("[-_ ]?thumbnail", RegexOption.IGNORE_CASE), "")
                                .trimEnd('-', '_', ' ')
                            else -> ""
                        }
                        WallpaperFormEntry(
                            imageFile = imageFile,
                            thumbnailFile = thumbnailFile,
                            panoramaFile = panoramaFile,
                            title = rawTitle,
                            series = globalSeries,
                        ).withAutoTags(globalTags)
                    }

                    // Replace the blank placeholder entry if nothing has been filled in yet
                    val isPlaceholder = entries.size == 1 &&
                            entries[0].imageFile == null && entries[0].thumbnailFile == null
                    val baseEntries = if (isPlaceholder) emptyList() else entries
                    val insertAt = baseEntries.size

                    entries = baseEntries + newEntries

                    // Async resolution + type detection per main image
                    newEntries.forEachIndexed { i, newEntry ->
                        val imageFile = newEntry.imageFile ?: return@forEachIndexed
                        detectResolutionAndTypeFromFile(imageFile) { resolution, type ->
                            entries = entries.toMutableList().also { list ->
                                val idx = insertAt + i
                                if (idx < list.size) {
                                    list[idx] = list[idx].copy(resolution = resolution, type = type)
                                        .withAutoTags(globalTags)
                                }
                            }
                        }
                    }
                }
            )

            // Rich error banner — shown when upload fails
            if (uploadState is WallpaperCRUDState.Error) {
                UploadErrorBanner(
                    error = uploadState as WallpaperCRUDState.Error,
                    entries = entries,
                    onDismiss = { viewModel.resetState() },
                )
            }

            // Series For All — sets the series on every entry at once
            CRUDFormField(
                label = "Series For All",
                value = globalSeries,
                placeholder = "Series applied to every entry (e.g. Frieren)",
                onValueChange = { globalSeries = it },
            )

            // Tags For All — extra tags appended to every entry
            CRUDFormField(
                label = "Tags For All",
                value = globalTags,
                placeholder = "Extra tags added to every entry (e.g. wallpaper | 4K)",
                onValueChange = { globalTags = it },
            )

            // Entry cards
            entries.forEachIndexed { index, entry ->
                WallpaperEntryCard(
                    index = index,
                    entry = entry,
                    globalTags = globalTags,
                    submitAttempted = submitAttempted,
                    onEntryChange = { updated ->
                        entries = entries.toMutableList().also { it[index] = updated }
                    },
                    onRemove = if (entries.size > 1) ({
                        entries = entries.toMutableList().also { it.removeAt(index) }
                    }) else null,
                )
            }

            // Add / Upload row
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(16.px),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                // Add another entry
                Box(
                    modifier = PressableButtonStyle.toModifier()
                        .padding(leftRight = 24.px, topBottom = 12.px)
                        .borderRadius(12.px)
                        .border(1.px, LineStyle.Solid, Colors.LightGray)
                        .onClick { entries = entries + WallpaperFormEntry(series = entries.first().series) },
                    contentAlignment = Alignment.Center,
                ) {
                    SpanText(
                        text = "+ Add Entry",
                        modifier = Modifier
                            .fontFamily(FontFamily)
                            .fontSize(14.px)
                            .color(DarkTextColor)
                    )
                }

                val allValid = entries.all { it.isValid }
                val isLoading = uploadState is WallpaperCRUDState.Loading

                GradientButton(
                    text = if (isLoading) "Uploading…" else "Upload All (${entries.size})",
                    disabled = isLoading,
                    onClick = {
                        submitAttempted = true
                        if (allValid && !isLoading) viewModel.uploadBatch(entries)
                    },
                )
            }
        }
    }
}

// ── Upload Error Banner ───────────────────────────────────────────────────────

/**
 * UploadErrorBanner
 * Detailed error display for batch upload failures.
 * Shows the failed entry, HTTP status, a user-friendly interpretation, and how many
 * entries uploaded successfully before the failure.
 *
 * @param error The error state from the ViewModel.
 * @param entries The full batch list — used to name the failing entry.
 * @param onDismiss Called when the user clicks the dismiss button.
 */
@Composable
private fun UploadErrorBanner(
    error: WallpaperCRUDState.Error,
    entries: List<WallpaperFormEntry>,
    onDismiss: () -> Unit,
) {
    val failedIndex = error.failedEntryIndex
    val failedEntry = failedIndex?.let { entries.getOrNull(it) }
    val successCount = failedIndex ?: 0

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .borderRadius(14.px)
            .backgroundColor(Colors.MistyRose)
            .border(1.px, LineStyle.Solid, Colors.LightCoral)
            .padding(leftRight = 20.px, topBottom = 16.px),
        verticalArrangement = Arrangement.spacedBy(10.px),
    ) {
        // Header row: title + dismiss
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            SpanText(
                text = "⚠ Upload Failed",
                modifier = Modifier
                    .fontFamily(FontFamily)
                    .fontSize(15.px)
                    .fontWeight(FontWeight.Bold)
                    .color(Colors.Crimson),
            )
            SpanText(
                text = "✕",
                modifier = PressableButtonStyle.toModifier()
                    .fontFamily(FontFamily)
                    .fontSize(14.px)
                    .color(Colors.Gray)
                    .onClick { onDismiss() },
            )
        }

        // Separator
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(1.px)
                .backgroundColor(Colors.LightCoral),
        )

        // Which entry failed
        if (failedEntry != null) {
            val entryLabel = if (failedEntry.title.isNotBlank())
                "Wallpaper #${failedIndex + 1} — \"${failedEntry.title}\""
            else
                "Wallpaper #${failedIndex + 1}"

            SpanText(
                text = "Failed at: $entryLabel",
                modifier = Modifier
                    .fontFamily(FontFamily)
                    .fontSize(13.px)
                    .fontWeight(FontWeight.SemiBold)
                    .color(Colors.Crimson),
            )
        }

        // Raw error message
        SpanText(
            text = "Error: ${error.message}",
            modifier = Modifier
                .fontFamily(FontFamily)
                .fontSize(13.px)
                .color(Colors.DarkRed),
        )

        // Status code + interpretation
        if (error.statusCode != 0) {
            val hint = statusCodeHint(error.statusCode)
            SpanText(
                text = "Status ${error.statusCode}: $hint",
                modifier = Modifier
                    .fontFamily(FontFamily)
                    .fontSize(13.px)
                    .color(Colors.IndianRed),
            )
        } else if (error.message.contains("network", ignoreCase = true) ||
                   error.message.contains("fetch", ignoreCase = true)) {
            SpanText(
                text = statusCodeHint(0),
                modifier = Modifier
                    .fontFamily(FontFamily)
                    .fontSize(13.px)
                    .color(Colors.IndianRed),
            )
        }

        // How many succeeded before this failure
        if (successCount > 0) {
            SpanText(
                text = "✓ $successCount of ${entries.size} wallpaper(s) uploaded successfully before this error.",
                modifier = Modifier
                    .fontFamily(FontFamily)
                    .fontSize(13.px)
                    .color(Colors.DarkGreen),
            )
        }
    }
}

// ── Batch Drop Zone ───────────────────────────────────────────────────────────

/**
 * BatchDropZone
 * Full-width multi-file drop area at the top of the add page.
 * Accepts any number of image files at once and fires [onFilesDropped] with the full list.
 * Click-to-browse is also supported (opens a multi-select file picker).
 *
 * @param onFilesDropped Called with all dropped/selected files.
 */
@Composable
private fun BatchDropZone(onFilesDropped: (List<File>) -> Unit) {
    var isDragOver by remember { mutableStateOf(false) }
    val callbackHolder = remember { object { var fn: (List<File>) -> Unit = {} } }
    callbackHolder.fn = onFilesDropped

    Div(attrs = {
        style {
            property("width", "100%")
            property("min-height", "160px")
            property("border", if (isDragOver) "2px dashed #f47b20" else "2px dashed #d0d0d0")
            property("border-radius", "16px")
            property("display", "flex")
            property("flex-direction", "column")
            property("align-items", "center")
            property("justify-content", "center")
            property("gap", "10px")
            property("padding", "32px 24px")
            property("cursor", "pointer")
            property("transition", "border-color 0.2s ease, background-color 0.2s ease")
            property("background-color", if (isDragOver) "#fff8f4" else "#fafafa")
            property("box-sizing", "border-box")
        }
        ref { element ->
            val collectFiles: (dynamic) -> List<File> = { e ->
                val fileList = e.dataTransfer.files
                val count = fileList.length as Int
                (0 until count).mapNotNull { fileList.item(it) as? File }
            }
            val onDragOver: (dynamic) -> Unit = { e -> e.preventDefault(); isDragOver = true }
            val onDragLeave: (dynamic) -> Unit = { _ -> isDragOver = false }
            val onDrop: (dynamic) -> Unit = { e ->
                e.preventDefault()
                isDragOver = false
                val files = collectFiles(e)
                if (files.isNotEmpty()) callbackHolder.fn(files)
            }
            element.addEventListener("dragover", onDragOver)
            element.addEventListener("dragleave", onDragLeave)
            element.addEventListener("drop", onDrop)
            onDispose {
                element.removeEventListener("dragover", onDragOver)
                element.removeEventListener("dragleave", onDragLeave)
                element.removeEventListener("drop", onDrop)
            }
        }
        onClick {
            val input = document.createElement("input")
            input.setAttribute("type", "file")
            input.setAttribute("accept", "image/*")
            input.setAttribute("multiple", "")
            input.asDynamic().onchange = { event: dynamic ->
                val fileList = event.target.files
                val count = fileList.length as Int
                val files = (0 until count).mapNotNull { fileList.item(it) as? File }
                if (files.isNotEmpty()) callbackHolder.fn(files)
                Unit
            }
            input.asDynamic().click()
        }
    }) {
        Span(attrs = {
            style {
                property("font-size", "36px")
                property("pointer-events", "none")
            }
        }) { Text(if (isDragOver) "📂" else "⬆") }

        Span(attrs = {
            style {
                property("font-family", "sans-serif")
                property("font-size", "15px")
                property("font-weight", "600")
                property("color", if (isDragOver) "#f47b20" else "#444")
                property("text-align", "center")
                property("pointer-events", "none")
            }
        }) { Text(if (isDragOver) "Release to create entries" else "Drop multiple images here to batch-create entries") }

        Span(attrs = {
            style {
                property("font-family", "sans-serif")
                property("font-size", "12px")
                property("color", "#999")
                property("text-align", "center")
                property("pointer-events", "none")
            }
        }) { Text("Same filename pairs files — e.g. frieren.jpg + frieren_thumbnail.jpg + frieren.webp (the .webp becomes the 360° panorama)") }
    }
}

/**
 * BatchFileGroup
 * One upload entry's files after name-based grouping.
 *
 * @param image Main wallpaper image, or null when only a thumbnail was dropped.
 * @param thumbnail Thumbnail file, or null when none matched.
 * @param panorama 360° panorama file, or null when none matched.
 */
private data class BatchFileGroup(
    val image: File?,
    val thumbnail: File?,
    val panorama: File?,
)

/**
 * groupFilesForBatch
 * Groups a flat file list into image / thumbnail / panorama sets by shared base name.
 * - A file whose stem contains "thumbnail" (case-insensitive) is a thumbnail; its base name is the
 *   stem with the thumbnail word and separators removed.
 * - Among the remaining files sharing a base name, a .webp becomes the panorama only when a
 *   non-webp image with the same base name exists. A lone .webp stays the main image.
 * Files with no match get their own entry with the other slots null.
 *
 * @param files Files dropped on the batch zone, in drop order.
 * @return One [BatchFileGroup] per base name, in first-seen order.
 */
private fun groupFilesForBatch(files: List<File>): List<BatchFileGroup> {
    val thumbnailPattern = Regex("[-_ ]?thumbnail", RegexOption.IGNORE_CASE)
    val thumbnails = linkedMapOf<String, File>()
    val mains = linkedMapOf<String, MutableList<File>>()

    for (file in files) {
        val stem = file.name.substringBeforeLast(".")
        if (thumbnailPattern.containsMatchIn(stem)) {
            thumbnails[stem.replace(thumbnailPattern, "").trimEnd('-', '_', ' ')] = file
        } else {
            mains.getOrPut(stem) { mutableListOf() }.add(file)
        }
    }

    val groups = linkedMapOf<String, BatchFileGroup>()
    for ((baseName, sameName) in mains) {
        val isWebp = { f: File -> f.name.substringAfterLast(".", "").equals("webp", ignoreCase = true) }
        val nonWebp = sameName.lastOrNull { !isWebp(it) }
        val webp = sameName.lastOrNull(isWebp)
        groups[baseName] = if (nonWebp != null && webp != null) {
            BatchFileGroup(image = nonWebp, thumbnail = thumbnails[baseName], panorama = webp)
        } else {
            BatchFileGroup(image = sameName.last(), thumbnail = thumbnails[baseName], panorama = null)
        }
    }
    for ((baseName, thumbnail) in thumbnails) {
        if (baseName !in groups) groups[baseName] = BatchFileGroup(null, thumbnail, null)
    }
    return groups.values.toList()
}

/** Returns a user-friendly action hint for a given HTTP status code. */
private fun statusCodeHint(code: Int): String = when (code) {
    400  -> "Invalid request — check that all required fields are filled correctly."
    401  -> "Unauthorized — the service requires authentication."
    403  -> "Forbidden — you do not have permission to upload."
    413  -> "File too large — try reducing the image size below 5 MB."
    415  -> "Unsupported file type — upload PNG or JPG images only."
    422  -> "Unprocessable content — one or more field values are invalid."
    429  -> "Too many requests — wait a moment and try again."
    500  -> "Server error — the service encountered an internal problem. Try again."
    502  -> "Bad gateway — the service may be restarting. Try again in a moment."
    503  -> "Service unavailable — the backend is temporarily down. Try again shortly."
    0    -> "Network error — verify the backend service is running on port 9090."
    else -> "Unexpected error (HTTP $code) — try again or check the backend logs."
}

// ── Entry Card ────────────────────────────────────────────────────────────────

/** Recomputes tags from title, series, type, description (+ optional global extra) joined by " | ". */
private fun WallpaperFormEntry.withAutoTags(globalTags: String = "") = copy(
    tags = (listOf(title, series, type, description) +
            if (globalTags.isNotBlank()) listOf(globalTags) else emptyList())
        .filter { it.isNotBlank() }
        .joinToString(" | ")
)

/**
 * WallpaperEntryCard
 * Card UI for a single wallpaper entry in the batch upload form.
 * Shows a validation hint row when [submitAttempted] is true and the entry is incomplete.
 *
 * @param index Zero-based position in the batch — used for the card heading.
 * @param entry Current form state for this entry.
 * @param globalTags Extra tags appended to every entry's auto-generated tag string.
 * @param submitAttempted True once the user has attempted to upload — enables validation hints.
 * @param onEntryChange Callback invoked with the updated entry on any field change.
 * @param onRemove Callback to remove this card; null when it is the only card.
 */
@Composable
private fun WallpaperEntryCard(
    index: Int,
    entry: WallpaperFormEntry,
    globalTags: String,
    submitAttempted: Boolean,
    onEntryChange: (WallpaperFormEntry) -> Unit,
    onRemove: (() -> Unit)?,
) {
    // Keep a fresh ref so async resolution callback sees the latest entry
    val entryRef = remember { object { var current = entry } }
    entryRef.current = entry

    val isInvalid = submitAttempted && !entry.isValid
    val borderColor = if (isInvalid) Colors.LightCoral else Colors.LightGray

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .border(1.px, LineStyle.Solid, borderColor)
            .borderRadius(16.px)
            .padding(all = 24.px),
        verticalArrangement = Arrangement.spacedBy(20.px),
    ) {
        // Card header
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            SpanText(
                text = "Wallpaper #${index + 1}",
                modifier = Modifier
                    .fontFamily(FontFamily)
                    .fontSize(16.px)
                    .fontWeight(FontWeight.SemiBold)
                    .color(DarkTextColor)
            )
            if (onRemove != null) {
                SpanText(
                    text = "✕ Remove",
                    modifier = PressableButtonStyle.toModifier()
                        .fontFamily(FontFamily)
                        .fontSize(13.px)
                        .color(Colors.Gray)
                        .onClick { onRemove() }
                )
            }
        }

        // Validation hint — only visible after a submit attempt on an invalid entry
        if (isInvalid) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(leftRight = 12.px, topBottom = 8.px)
                    .borderRadius(8.px)
                    .backgroundColor(Colors.MistyRose)
                    .border(1.px, LineStyle.Solid, Colors.LightCoral),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(6.px),
            ) {
                SpanText(
                    text = "Missing required: ${entry.missingFields.joinToString(" · ")}",
                    modifier = Modifier
                        .fontFamily(FontFamily)
                        .fontSize(12.px)
                        .color(Colors.Crimson),
                )
            }
        }

        // Drop zones
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(16.px),
        ) {
            Box(modifier = Modifier.weight(1f)) {
                CRUDDropZone(
                    label = "Image",
                    file = entry.imageFile,
                    onFileDropped = { file ->
                        detectResolutionAndTypeFromFile(file) { resolution, type ->
                            val current = entryRef.current
                            // Pre-fill title from filename (strip extension) only when the field is blank
                            val autoTitle = if (current.title.isBlank())
                                file.name.substringBeforeLast(".") else current.title
                            onEntryChange(
                                current.copy(imageFile = file, resolution = resolution, type = type, title = autoTitle)
                                    .withAutoTags(globalTags)
                            )
                        }
                    },
                    onFileRemoved = { onEntryChange(entry.copy(imageFile = null, resolution = "").withAutoTags(globalTags)) },
                )
            }
            Box(modifier = Modifier.weight(1f)) {
                CRUDDropZone(
                    label = "Thumbnail",
                    file = entry.thumbnailFile,
                    onFileDropped = { onEntryChange(entry.copy(thumbnailFile = it).withAutoTags(globalTags)) },
                    onFileRemoved = { onEntryChange(entry.copy(thumbnailFile = null).withAutoTags(globalTags)) },
                )
            }
            Box(modifier = Modifier.weight(1f)) {
                CRUDDropZone(
                    label = "Panorama (360°, optional)",
                    file = entry.panoramaFile,
                    onFileDropped = { onEntryChange(entry.copy(panoramaFile = it)) },
                    onFileRemoved = { onEntryChange(entry.copy(panoramaFile = null)) },
                )
            }
        }

        // Row 1: Title + Type dropdown
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(16.px),
        ) {
            Box(modifier = Modifier.weight(1f)) {
                CRUDFormField(
                    label = "Title *",
                    value = entry.title,
                    placeholder = "e.g. Frieren Season 2",
                    onValueChange = { onEntryChange(entry.copy(title = it).withAutoTags(globalTags)) },
                )
            }
            Box(modifier = Modifier.weight(1f)) {
                CRUDTypeDropdown(
                    selected = entry.type,
                    onSelected = { onEntryChange(entry.copy(type = it).withAutoTags(globalTags)) },
                )
            }
        }

        // Row 2: Series + Resolution (read-only)
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(16.px),
        ) {
            Box(modifier = Modifier.weight(1f)) {
                CRUDFormField(
                    label = "Series *",
                    value = entry.series,
                    placeholder = "e.g. Frieren",
                    onValueChange = { onEntryChange(entry.copy(series = it).withAutoTags(globalTags)) },
                )
            }
            Box(modifier = Modifier.weight(1f)) {
                CRUDFormField(
                    label = "Resolution (auto)",
                    value = entry.resolution,
                    placeholder = "Detected on image drop",
                    readOnly = true,
                    onValueChange = {},
                )
            }
        }

        // Row 3: Tags (auto) + Video Link
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(16.px),
        ) {
            Box(modifier = Modifier.weight(1f)) {
                CRUDFormField(
                    label = "Tags (auto)",
                    value = entry.tags,
                    placeholder = "Auto-generated from title, series, type, description",
                    readOnly = true,
                    onValueChange = {},
                )
            }
            Box(modifier = Modifier.weight(1f)) {
                CRUDFormField(
                    label = "Video Link",
                    value = entry.videoLink,
                    placeholder = "YouTube URL (optional)",
                    onValueChange = { onEntryChange(entry.copy(videoLink = it)) },
                )
            }
        }

        CRUDFormField(
            label = "Description",
            value = entry.description,
            placeholder = "Short description of the wallpaper",
            onValueChange = { onEntryChange(entry.copy(description = it).withAutoTags(globalTags)) },
        )
    }
}
